/**
 * Version Error Dialog
 *
 * Thin wrapper around the shared ErrorDialog for failed version uploads.
 * Parses version error data and passes it to the shared dialog.
 */

import { useMemo } from 'react';

import { ErrorDialog, parseErrorToRows, formatErrorCount } from '@/components/common';
import { formatDateTimeShort } from '@/lib/formatters';

interface VersionErrorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: {
    version_number: number;
    file_name: string;
    created_at: string;
    error_message: string;
    error_details?: Record<string, unknown> | null;
  } | null;
}

export function VersionErrorDialog({
  open,
  onOpenChange,
  version,
}: VersionErrorDialogProps) {
  const { rows, isTruncated } = useMemo(() => {
    if (!version) return { rows: [], isTruncated: false };
    return parseErrorToRows(version.error_message, version.error_details);
  }, [version]);

  return (
    <ErrorDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Upload Failed"
      description={
        version && (
          <span className="flex items-center gap-2 text-sm">
            <span className="font-medium">V{version.version_number}</span>
            <span className="text-muted-foreground/40">/</span>
            <span className="truncate">{version.file_name}</span>
            <span className="text-muted-foreground/40">/</span>
            <span className="shrink-0">{formatDateTimeShort(version.created_at)}</span>
          </span>
        )
      }
      rows={rows}
      isTruncated={isTruncated}
      exportFileName={`errors-v${version?.version_number ?? 0}.xlsx`}
      footerMessage={`${formatErrorCount(rows.length, isTruncated)} found. Fix the issues and re-upload.`}
    />
  );
}
